/* eslint-disable react-hooks/exhaustive-deps */
import React, {
  useState,
  useEffect,
  useContext,
  createContext,
  useCallback,
} from 'react';
import { useTranslation } from 'next-i18next';
import { connect } from 'react-redux';
import { useRouter } from 'next/router';
import { useAuthRefresh, type RefreshAuthToken } from './hooks/use-auth-refresh';

const AuthContext = createContext<{
  currentUser: any;
  loading: boolean;
}>({
  currentUser: null,
  loading: true,
});

function mapStateToProps(state: any) {
  const { auth } = state;
  const { user, loading, loaded } = auth;
  return {
    user,
    loading,
    loaded,
  };
}

interface AuthProviderProps {
  user: any;
  loaded: boolean;
  fetchCurrentUser: () => void;
  refreshAuthToken?: RefreshAuthToken;
  children: React.ReactNode;
}

const AuthProviderInner = (props: AuthProviderProps) => {
  const { user, fetchCurrentUser, loaded, refreshAuthToken, children } = props;
  const router = useRouter();
  const { i18n } = useTranslation();
  const [curUser, setUser] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true);
  const handleNoSession = useCallback(() => setLoadingUser(false), []);

  useAuthRefresh({
    fetchCurrentUser,
    refreshAuthToken,
    onNoSession: handleNoSession,
  });
  useEffect(() => {
    if (loaded) {
      setLoadingUser(false);
      if (user) {
        const userLocale = user.locale?.i18n_form;
        if (userLocale) {
          i18n.changeLanguage(userLocale);
          router.replace(router.pathname, router.asPath, {
            locale: userLocale,
          });
        }
      }
    }
  }, [loaded]);
  useEffect(() => {
    if (user && !curUser) {
      setUser(user);
    }
    if (!user && curUser) {
      setUser(null);
    }
  }, [user]);

  return (
    <AuthContext.Provider value={{ currentUser: user, loading: loadingUser }}>
      {children}
    </AuthContext.Provider>
  );
};

const ConnectedAuthProvider = connect(mapStateToProps)(AuthProviderInner);

export interface AuthProviderWrapperProps {
  fetchCurrentUser: () => void;
  refreshAuthToken?: () => Promise<string | null> | string | null | void;
  children: React.ReactNode;
}

/**
 * AuthProvider component that wraps Redux state with auth context.
 * Must be wrapped in a Redux Provider and provided with the fetchCurrentUser action.
 *
 * @example
 * import { AuthProvider } from '@lb-classroom/feature-auth';
 * import * as CurrentUserActions from './actions/currentUser';
 *
 * <AuthProvider fetchCurrentUser={currentUserActions.fetchCurrentUser}>
 *   <App />
 * </AuthProvider>
 */
export const AuthProvider = ({
  fetchCurrentUser,
  refreshAuthToken,
  children,
}: AuthProviderWrapperProps) => {
  return (
    <ConnectedAuthProvider
      fetchCurrentUser={fetchCurrentUser}
      refreshAuthToken={refreshAuthToken}
    >
      {children}
    </ConnectedAuthProvider>
  );
};

export default AuthProvider;

export const useAuth = () => {
  return useContext(AuthContext);
};
